import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Input,
  InputNumber,
  Modal,
  message,
  Select,
  Space,
  Spin,
  Table,
  Divider,
  Tooltip,
} from "antd";
import { CheckCircleOutlined, CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import { getRequest, postRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import { authUserData } from "../../../utils/Helper";
import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";

const { TextArea } = Input;

const REASON_OPTIONS = [
  { label: "Damaged on Site", value: "Damaged on Site" },
  { label: "QC Rejection", value: "QC Rejection" },
  { label: "Transit Damage", value: "Transit Damage" },
  { label: "Wrong Item Received", value: "Wrong Item Received" },
];

const DESTINATION_OPTIONS = [
  { label: "To Supplier (Replacement)", value: "To Supplier (Replacement)" },
  { label: "Scrap / Disposal", value: "Scrap / Disposal" },
  { label: "Return to Central Store", value: "Return to Central Store" },
];

const InventoryItemReturnEntry = () => {
  const user = authUserData();
  const [searchValue, setSearchValue] = useState("");
  const [draft, setDraft] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [stockSites, setStockSites] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [quarantineLookup, setQuarantineLookup] = useState({});

  const userDisplay = useMemo(() => {
    const first = (user?.first_name || "").trim();
    const last = (user?.last_name || "").trim();
    const fullName = `${first} ${last}`.trim();
    return (
      fullName || user?.email || user?.username || user?.mobile_number || "—"
    );
  }, [user]);

  const fetchCurrentStocks = async (params = {}) => {
    try {
      setLoadingStocks(true);
      const res = await getRequest(
        "replacement-pos/site-wise-accepted-materials/",
        params,
      );
      const payload = res?.data;
      const results = payload?.results;
      const rawList =
        results?.data ??
        payload?.data ??
        (Array.isArray(payload) ? payload : []);
      const list = Array.isArray(rawList) ? rawList : [];
      setStockSites(list);
    } catch (err) {
      ShowErrorMsg(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load current stocks",
      );
      setStockSites([]);
    } finally {
      setLoadingStocks(false);
    }
  };

  const fetchQuarantineLookup = async (params = {}) => {
    try {
      const res = await getRequest("quarantined-materials/", params);
      const payload = res?.data;
      const results = payload?.results;
      const rawList =
        results?.data ??
        payload?.data ??
        (Array.isArray(payload) ? payload : []);
      const list = Array.isArray(rawList) ? rawList : [];

      const next = {};
      for (const item of list) {
        const siteName = String(item?.site_name ?? "").trim();
        const sourceRef = String(item?.source_ref ?? "").trim(); // e.g. "Satellite - GRN-2026-009"
        const itemName = String(item?.item_name ?? "").trim();
        const uom = String(item?.uom ?? "").trim();
        const id = item?.id ?? null;

        if (!siteName || !sourceRef || !itemName || id == null) continue;

        const key = `${sourceRef}||${itemName}||${uom}`.toLowerCase();
        next[key] = id;
      }
      setQuarantineLookup(next);
    } catch (err) {
      // Non-blocking: selection will just be disabled if we can't resolve IDs
      setQuarantineLookup({});
    }
  };

  useEffect(() => {
    fetchCurrentStocks({ page: 1, pagesize: 50 });
    // fetchQuarantineLookup({ page: 1, pagesize: 1000 });
  }, []);

  const flattenStocks = useMemo(() => {
    return (stockSites || []).flatMap((site) => {
      const mats = Array.isArray(site?.materials) ? site.materials : [];
      return mats.map((m, idx) => {
        const key = `${site?.site_id ?? "site"}-${m?.grn_number ?? "grn"}-${m?.item_name ?? "item"}-${idx}`;
        return {
          key,
          site_id: site?.site_id,
          site_name: site?.site_name ?? "—",
          id: m?.id ?? null,
          quarantined_material_id:
            m?.quarantined_material_id ??
            m?.quarantinedMaterialId ??
            m?.id ??
            null,
          itemName: m?.item_name ?? "—",
          itemSpecification: m?.item_specification ?? null,
          uom: m?.uom ?? "—",
          distributedQuantity: Number(m?.distributed_quantity) || 0,
          actualReceivedQuantity: Number(m?.actual_received_quantity) || 0,
          transitRejectQuantity: Number(m?.transit_reject_quantity) || 0,
          status: m?.status ?? "—",
          confirmationDate: m?.confirmation_date ?? "—",
          vendorName: m?.vendor_name ?? "—",
          invoiceNumber: m?.invoice_number ?? "—",
          grnNumber: m?.grn_number ?? "—",
          sourceRef: `${site?.site_name ?? "—"} - ${m?.grn_number ?? "—"}`,
          available: Number(m?.actual_received_quantity) || 0,
        };
      });
    });
  }, [stockSites]);

  const filteredSites = useMemo(() => {
    const q = (searchValue || "").trim().toLowerCase();
    const sites = Array.isArray(stockSites) ? stockSites : [];

    return sites
      .map((site) => {
        const materials = Array.isArray(site?.materials) ? site.materials : [];
        const mappedMaterials = materials.map((m, idx) => {
          const key = `${site?.site_id ?? "site"}-${m?.grn_number ?? "grn"}-${m?.item_name ?? "item"}-${idx}`;
          const sourceRef = `${site?.site_name ?? "—"} - ${m?.grn_number ?? "—"}`;
          const lookupKey =
            `${sourceRef}||${String(m?.item_name ?? "—")}||${String(m?.uom ?? "—")}`.toLowerCase();
          const quarantinedId = quarantineLookup?.[lookupKey] ?? null;
          return {
            key,
            site_id: site?.site_id,
            site_name: site?.site_name ?? "—",
            id: m?.id ?? null,
            quarantined_material_id:
              m?.quarantined_material_id ??
              m?.quarantinedMaterialId ??
              quarantinedId ??
              null,
            itemName: m?.item_name ?? "—",
            itemSpecification: m?.item_specification ?? null,
            uom: m?.uom ?? "—",
            distributedQuantity: Number(m?.distributed_quantity) || 0,
            actualReceivedQuantity: Number(m?.actual_received_quantity) || 0,
            transitRejectQuantity: Number(m?.transit_reject_quantity) || 0,
            status: m?.status ?? "—",
            confirmationDate: m?.confirmation_date ?? "—",
            vendorName: m?.vendor_name ?? "—",
            invoiceNumber: m?.invoice_number ?? "—",
            grnNumber: m?.grn_number ?? "—",
            sourceRef,
            available: Number(m?.actual_received_quantity) || 0,
          };
        });

        const filteredMaterials = !q
          ? mappedMaterials
          : mappedMaterials.filter(
              (m) =>
                String(m?.itemName ?? "")
                  .toLowerCase()
                  .includes(q) ||
                String(m?.grnNumber ?? "")
                  .toLowerCase()
                  .includes(q) ||
                String(m?.invoiceNumber ?? "")
                  .toLowerCase()
                  .includes(q) ||
                String(site?.site_name ?? "")
                  .toLowerCase()
                  .includes(q),
            );

        return {
          key: site?.site_id ?? `site-${site?.site_name ?? ""}`,
          site_id: site?.site_id,
          site_name: site?.site_name ?? "—",
          total_materials: site?.total_materials ?? filteredMaterials.length,
          total_quantity: site?.total_quantity ?? null,
          materials: filteredMaterials,
        };
      })
      .filter((s) => (s?.materials ?? []).length > 0);
  }, [quarantineLookup, searchValue, stockSites]);

  const addToDraft = (item) => {
    setDraft((prev) => {
      const exists = prev.find((row) => row.key === item.key);
      if (exists) return prev;
      return [
        ...prev,
        {
          ...item,
          returnQty: undefined,
          reason: undefined,
          destination: undefined,
          shipping_method: "",
          notes: "",
        },
      ];
    });
  };

  const updateDraft = (key, field, value) => {
    setDraft((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  };

  const removeDraft = (key) => {
    setDraft((prev) => prev.filter((row) => row.key !== key));
  };

  const disableSubmit =
    submitting ||
    draft.length === 0 ||
    draft.some(
      (row) =>
        !row.returnQty ||
        row.returnQty > row.available ||
        !String(row?.destination || "").trim() ||
        !String(row?.shipping_method || "").trim(),
    );

  const handleSubmitReturnRequests = async () => {
    try {
      if (submitting) return;
      if (!draft.length) {
        message.error("Please select at least one item for return.");
        return;
      }

      const invalidRow = draft.find((row) => {
        const qty = Number(row?.returnQty) || 0;
        const available = Number(row?.available) || 0;
        const destinationOk = Boolean(String(row?.destination || "").trim());
        const shippingOk = Boolean(String(row?.shipping_method || "").trim());
        return qty <= 0 || qty > available || !destinationOk || !shippingOk;
      });

      if (invalidRow) {
        message.error(
          `Please verify Return Qty, Destination, and Shipping Method for "${invalidRow?.itemName ?? "Item"}".`,
        );
        return;
      }

      const confirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: "Submit return request?",
          content: `This will submit ${draft.length} item(s) for return and generate RA ID.`,
          okText: "Yes, submit",
          cancelText: "Cancel",
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;

      setSubmitting(true);

      const payload = draft.map((row) => {
        const rawQuarantinedMaterialId = row?.quarantined_material_id ?? null;
        const quarantinedMaterialId =
          rawQuarantinedMaterialId == null ||
          Number.isNaN(Number(rawQuarantinedMaterialId))
            ? null
            : Number(rawQuarantinedMaterialId);

        return {
          quarantined_material_id: quarantinedMaterialId,
          source_ref: row?.sourceRef ?? "",
          return_qty: row?.returnQty ?? 0,
          uom: row?.uom ?? "",
          intended_destination: row?.destination ?? "",
          shipping_method: row?.shipping_method?.trim() ?? "",
          notes: row?.notes?.trim() ?? "",
        };
      });

      const res = await postRequest("return-requests/", payload);

      const data = res?.data?.data ?? res?.data?.results ?? res?.data;
      const list = Array.isArray(data) ? data : data?.data;
      const raIds = Array.isArray(list)
        ? list.map((x) => x?.ra_id).filter(Boolean)
        : [data?.ra_id].filter(Boolean);

      message.success({
        content: raIds.length
          ? `Submitted ${payload.length} return request(s): ${raIds.join(", ")}`
          : (res?.data?.message ??
            `Submitted ${payload.length} return request(s) successfully`),
        key: "return-submit",
        duration: 6,
      });
      setDraft([]);
      fetchCurrentStocks({ page: 1, pagesize: 50 });
    } catch (err) {
      message.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to submit return request",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const availableColumns = [
    {
      title: "Site",
      dataIndex: "site_name",
      key: "site_name",
      render: (val, record) => (
        <div>
          <div style={{ fontWeight: 700 }}>{val}</div>
        </div>
      ),
    },
    {
      title: "No. of Materials",
      dataIndex: "total_materials",
      key: "total_materials",
      width: 140,
      align: "center",
      render: (val) => <span>{Number(val) || 0}</span>,
    },
    {
      title: "Total Qty.",
      dataIndex: "total_quantity",
      key: "total_quantity",
      width: 140,
      align: "center",
      render: (val) => (val == null ? "—" : Number(val) || 0),
    },
  ];

  const materialColumns = [
    {
      title: "Item",
      dataIndex: "itemName",
      key: "itemName",
      render: (val) => <span>{val ?? "—"}</span>,
    },
    {
      title: "Available Qty.",
      dataIndex: "available",
      key: "available",
      // width: 120,
      align: "center",
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      key: "uom",
      // width: 90,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      // width: 170,
    },
    {
      title: "GRN & Invoice",
      key: "grn_invoice",
      // width: 190,
      render: (_, record) => (
        <div>
          <div>{record?.grnNumber ?? "—"}</div>
          <div className="muted-text">
            {record?.invoiceNumber && record.invoiceNumber !== "—"
              ? `Invoice: ${record.invoiceNumber}`
              : "Invoice: —"}
          </div>
        </div>
      ),
    },
    {
      title: "Vendor Name",
      dataIndex: "vendorName",
      key: "vendorName",
      // width: 200,
      ellipsis: true,
      render: (val) => val ?? "—",
    },
    {
      title: "Confirmed On",
      dataIndex: "confirmationDate",
      key: "confirmationDate",
      // width: 140,
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      align: "center",
      render: (_, record) => {
        const alreadySelected = draft.some((d) => d?.key === record?.key);
        const hasStock = (Number(record?.available) || 0) > 0;
        const disabled = !hasStock || alreadySelected;
        const tooltip = !hasStock
          ? "No available stock"
          : alreadySelected
            ? "Already added to draft"
            : "Select for return";

        return (
          <Tooltip title={tooltip || null}>
            <span
              role="button"
              tabIndex={0}
              aria-disabled={disabled}
              onClick={() => {
                if (disabled) return;
                addToDraft(record);
              }}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") addToDraft(record);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                // borderRadius: 8,
                // border: "1px solid #d9d9d9",
                cursor: disabled ? "not-allowed" : "pointer",
                color: disabled ? "#bfbfbf" : "#094e8f",
                background: "#fff",
              }}
            >
              {alreadySelected ? (
                <CheckCircleOutlined style={{ fontSize: 18 }} />
              ) : (
                <PlusCircleOutlined style={{ fontSize: 18 }} />
              )}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const draftColumns = [
    {
      title: "Site",
      dataIndex: "site_name",
      key: "site_name",
      // width: 170,
      render: (val) => val ?? "—",
    },
    {
      title: "Item",
      dataIndex: "itemName",
      key: "itemName",
      // width: 240,
      render: (_, record) => (
        <div>
          <div>{record.itemName ?? "—"}</div>
        </div>
      ),
    },
    {
      title: "Available Qty.",
      dataIndex: "available",
      key: "available",
      // width: 140,
      align: "right",
    },
    {
      title: "Return Qty.",
      dataIndex: "returnQty",
      key: "returnQty",
      // width: 150,
      render: (_, record) => (
        <div>
          <InputNumber
            style={{ width: "50%" }}
            min={0}
            max={record.available}
            placeholder="Return qty."
            value={
              record.returnQty === null ||
              record.returnQty === undefined ||
              record.returnQty === ""
                ? undefined
                : record.returnQty
            }
            status={
              record.returnQty != null &&
              record.returnQty !== "" &&
              record.returnQty > record.available
                ? "error"
                : undefined
            }
            onChange={(val) =>
              updateDraft(
                record.key,
                "returnQty",
                val === null || val === undefined ? undefined : val,
              )
            }
          />
        </div>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      // width: 200,
      render: (_, record) => (
        <Select
          allowClear
          placeholder="Select reason"
          value={
            record.reason === null ||
            record.reason === undefined ||
            record.reason === ""
              ? undefined
              : record.reason
          }
          options={REASON_OPTIONS}
          onChange={(val) =>
            updateDraft(record.key, "reason", val === undefined ? undefined : val)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Destination",
      dataIndex: "destination",
      key: "destination",
      // width: 220,
      render: (_, record) => (
        <Select
          allowClear
          placeholder="Select destination"
          value={
            record.destination === null ||
            record.destination === undefined ||
            record.destination === ""
              ? undefined
              : record.destination
          }
          options={DESTINATION_OPTIONS}
          onChange={(val) =>
            updateDraft(
              record.key,
              "destination",
              val === undefined ? undefined : val,
            )
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Shipping Method",
      dataIndex: "shipping_method",
      key: "shipping_method",
      // width: 180,
      render: (_, record) => (
        <Input
          placeholder="Shipping method"
          value={record.shipping_method}
          onChange={(e) =>
            updateDraft(record.key, "shipping_method", e.target.value)
          }
        />
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 320,
      render: (_, record) => (
        <TextArea
          rows={2}
          value={record.notes}
          onChange={(e) => updateDraft(record.key, "notes", e.target.value)}
          placeholder="Bags got wet due to rain leaking in storage area."
        />
      ),
    },
    {
      title: "Remove",
      key: "remove",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Tooltip title="Remove from draft" placement="left">
          <Button
            type="text"
            icon={<CloseOutlined />}
            danger
            onClick={() => removeDraft(record.key)}
            aria-label="Remove from draft"
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip
              placement="right"
              title="Create a return request for selected inventory items."
            >
              <h3>Inventory Based Return</h3>
            </Tooltip>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Inventory Based Return</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>
      <section className="grid-sec FormSection Container aidocintak">
        <Spin spinning={submitting} tip="Submitting return request...">
          <div className="FormBody">
            <div className="stepscontentwrap" style={{ padding: 0 }}>
              <div className="stepsContent" style={{ paddingRight: "5px" }}>
           
              <Divider
                orientation="left"
                plain
                orientationMargin={0}
                style={{ marginBottom: 0 }}
              >
                <div className="returninfo-title">
                  <span className="count">1</span> Search Available Inventory to
                  Return
                </div>
              </Divider>

              <div
                className="tablewrp"
                style={{
                  paddingLeft: "34px",
                  marginTop: "10px",
                  marginBottom: "24px",
                }}
              >
                <Table
                  rowKey="key"
                  columns={availableColumns}
                  dataSource={filteredSites}
                  pagination={false}
                  scroll={{ x: 800 }}
                  size="small"
                  loading={loadingStocks}
                  expandable={{
                    rowExpandable: (record) =>
                      (record?.materials ?? []).length > 0,
                    expandedRowRender: (record) => (
                      <div style={{ padding: "6px 8px" }}>
                        <Table
                          rowKey="key"
                          columns={materialColumns}
                          dataSource={record?.materials ?? []}
                          pagination={false}
                          size="small"
                          scroll={{ x: 1000 }}
                          locale={{ emptyText: "No materials found." }}
                        />
                      </div>
                    ),
                  }}
                />
              </div>

              <Divider
                orientation="left"
                plain
                orientationMargin={0}
                style={{ marginBottom: 0 }}
              >
                <div className="returninfo-title">
                  <span className="count">2</span> Return Request Draft (Items
                  selected for return)
                </div>
              </Divider>
              <div
                className="tablewrp"
                style={{
                  paddingLeft: "34px",
                  marginTop: "10px",
                  marginBottom: "24px",
                }}
              >
                <Table
                  rowKey="key"
                  columns={draftColumns}
                  dataSource={draft}
                  pagination={false}
                  // scroll={{ x: 1000 }}
                  size="small"
                  locale={{
                    emptyText:
                      "No items selected. Use 'Select for Return' to add items.",
                  }}
                />
              </div>
            </div>
            <div className="FormFooter">
              <div className="good-tge">
                <Alert
                  type="info"
                  showIcon
                  style={{
                    width: "100%",
                    padding: "5px 10px",
                    borderRadius: 4,
                  }}
                  message={
                    <span>
                      System Action: On submission, selected items will
                      immediately move from &apos;Available&apos; to
                      &apos;Quarantine / Pending Return&apos; status at the
                      selected site.
                    </span>
                  }
                />
              </div>
              <Space style={{ justifyContent: "flex-end" }}>
                <Button
                  type="primary"
                  className="SaveGradiantBtn"
                  disabled={disableSubmit}
                  loading={submitting}
                  onClick={handleSubmitReturnRequests}
                >
                  Submit Return Request (Generate RA ID)
                </Button>
              </Space>
              </div>
            </div>
          </div>
        </Spin>
      </section>
    </>
  );
};

export default InventoryItemReturnEntry;
